// Send.tsx
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  Text,
  Loader,
  Stack,
  Center,
  Textarea,
  NumberInput,
  TextInput,
  Title,
  Group,
  Button,
  ActionIcon,
  Alert,
  rem,
} from "@mantine/core";
import { BobsWorkspace } from "../stores/BobsWorkspace";
import TwoFactorInput from "./details/twoFactorInput";
import { NumericKeypad } from "./details/numericKeypad";
import AmountStagedForPayAttempt from "./details/amountStagedForPayment";
import {
  IconRefresh,
  IconSend2,
  IconSettings,
  IconX,
  IconQrcode,
  IconHistory,
  IconInfoCircle,
  IconBolt,
} from "@tabler/icons-react";
import QrScannerSection from "./details/QRScannerSection";
import RecentRecipientsMenu from "./details/recentRecipients";
const Send = observer(({ bobsWorkspace }: { bobsWorkspace: BobsWorkspace }) => {
  const selectedBob = bobsWorkspace.selectedBob;
  const [twoFACode, setTwoFACode] = useState<string>("");

  useEffect(() => {
    selectedBob.makeNewPayAttemptIfNecessary();
    selectedBob.checkIfWeNeedToShowTwoFactorForm();
  }, []);

  const selectedPayAttempt = selectedBob?.selectedPayAttempt;

  useEffect(() => {
    if (selectedPayAttempt) {
      // Only log when button loading state changes
      const isLoading =
        selectedPayAttempt.waitingOnRailsToProvidePayAttemptUuid;
      // console.log("[PAYMENT-FLOW] 🔄 Button:", isLoading ? "loading" : "ready");
    }
  }, [
    selectedPayAttempt?.waitingOnRailsToProvidePayAttemptUuid,
    selectedPayAttempt?.payAttemptStatus,
    selectedBob.safeBalanceInSatoshis,
  ]);
  // const handleDetected = (raw: string) =>
  const handleDetected = (raw: string) => {
    const cleaned = raw
      .trim()
      .replace(/\s+/g, "")
      .replace(/^lightning:/i, "");
    selectedPayAttempt?.setUserEnteredPaymentString(cleaned);
  };

  // keypad state for zero-amount invoices
  const [draftAmount, setDraftAmount] = useState<number>(0);
  const needsOk = selectedPayAttempt?.payAttemptStatus === "needs-pay-amount";

  const startOver = () => {
    setTwoFACode("");
    setDraftAmount(0);
    selectedBob.refreshPayAttempt();
  };
  // current amount & validation
  const workingAmount = needsOk
    ? draftAmount
    : (selectedPayAttempt?.payAmountSatoshis ?? 0);
  const tooHigh =
    workingAmount + (selectedPayAttempt?.maxRoutingFeeSatoshis ?? 0) >
    (selectedPayAttempt?.bob.safeBalanceInSatoshis ?? 0);
  const KEYPAD_MAX_WIDTH = 340;
  const INVOICE_ENTRY_MAX_WIDTH = 420;

  return (
    <>
      <Center>
        <Group gap="xs">
          <IconBolt size={28} />
          <Title order={2}>Send Lightning</Title>
        </Group>
      </Center>

      {selectedPayAttempt && (
        <Stack>
          {!selectedPayAttempt.hidePayattemptTextEntryForm && (
            <>
              <Center>
                <Textarea
                  placeholder="Paste a lightning invoice or address here"
                  value={selectedPayAttempt.userEnteredPaymentString}
                  onChange={(e) => {
                    let val = e.currentTarget.value.replace(/[\r\n\s]+/g, "");
                    // Downcase lightning addresses
                    if (val.includes("@")) {
                      val = val.toLowerCase();
                    }
                    selectedPayAttempt.setUserEnteredPaymentString(val);
                  }}
                  error={selectedPayAttempt.userFacingErrorMessage}
                  readOnly={selectedPayAttempt.hidePayattemptTextEntryForm}
                  minRows={4}
                  maxRows={6}
                  autosize
                  style={{
                    maxWidth: rem(INVOICE_ENTRY_MAX_WIDTH),
                    width: "100%",
                  }}
                />
              </Center>
            </>
          )}

          {selectedPayAttempt.showAmountStagedForPayment && (
            <>
              <AmountStagedForPayAttempt attempt={selectedPayAttempt} />
              {selectedPayAttempt.userFacingErrorMessage && (
                <Alert
                  //variant="light"
                  color="red"
                  title="Error"
                  icon={<IconInfoCircle size={16} />}
                >
                  {selectedPayAttempt.userFacingErrorMessage}
                </Alert>
              )}
            </>
          )}

          {!selectedPayAttempt.hidePayattemptTextEntryForm && (
            <>
              <Group justify="center" gap="md" mt="md">
                <QrScannerSection onDetected={handleDetected} />
                <RecentRecipientsMenu
                  recentAddresses={selectedBob.recentlyPaidLightningAddresses}
                  payAttempt={selectedPayAttempt}
                />
              </Group>
            </>
          )}

          {selectedPayAttempt.showLnurlPaymentLoader && (
            <Loader
              size="xl"
              variant="dots"
              color="blue"
              style={{ margin: "auto" }}
            />
          )}

          {selectedPayAttempt.shouldShowNumericKeypadForInvoice && (
            <Center>
              <NumericKeypad
                currencyCode={selectedBob.currencyCode}
                currencySnapshot={selectedBob.workspace.exchangeRates.data}
                initialMode="sats"
                valueSats={workingAmount}
                minSats={1}
                maxSats={selectedPayAttempt.bob.safeBalanceInSatoshis}
                readOnly={
                  selectedPayAttempt.payAmountNumberInputShouldBeReadOnly
                }
                error={
                  tooHigh
                    ? "Amount is too high"
                    : selectedPayAttempt.userFacingErrorMessage
                }
                onChangeSats={(s) =>
                  needsOk
                    ? setDraftAmount(s)
                    : selectedPayAttempt.setPayAmountSatoshis(s)
                }
                onOk={
                  needsOk
                    ? (s) => selectedPayAttempt.setPayAmountSatoshis(s)
                    : undefined
                }
              />
            </Center>
          )}

          {selectedPayAttempt.shouldShowLnurlStageTwoForm && (
            <>
              <Center>
                <NumericKeypad
                  currencyCode={selectedBob.currencyCode}
                  currencySnapshot={selectedBob.workspace.exchangeRates.data}
                  initialMode="sats"
                  valueSats={
                    selectedPayAttempt.userProposedLnurlPaymentAmount ?? 0
                  }
                  minSats={Math.max(
                    selectedPayAttempt.lnUrlOneResponse.data
                      .minSendableSatoshis,
                    1,
                  )}
                  maxSats={Math.min(
                    selectedPayAttempt.bob.safeBalanceInSatoshis,
                    selectedPayAttempt.lnUrlOneResponse.data
                      .maxSendableSatoshis,
                  )}
                  readOnly={
                    selectedPayAttempt.shouldDisableLnrulStageTwoAmountInput
                  }
                  error={
                    selectedPayAttempt.userProposedLnurlPaymentAmountIsToohigh
                      ? "Amount is too high"
                      : selectedPayAttempt.userFacingErrorMessage
                  }
                  onChangeSats={(s) =>
                    selectedPayAttempt.setUserProposedLnurlPaymentAmount(s)
                  }
                />
              </Center>

              {selectedPayAttempt.lnUrlOneResponse.data.plainTextForUserDisplay
                .length > 0 && (
                <Center>
                  <Stack
                    style={{ maxWidth: rem(KEYPAD_MAX_WIDTH), width: "100%" }}
                  >
                    <Text style={{ wordBreak: "break-all" }}>
                      {selectedPayAttempt.userEnteredPaymentString.trim()}
                    </Text>
                    <Text>
                      {'"'}
                      {
                        selectedPayAttempt.lnUrlOneResponse.data
                          .plainTextForUserDisplay
                      }
                      {'"'}
                    </Text>
                  </Stack>
                </Center>
              )}
              <Center>
                <Stack
                  style={{ maxWidth: rem(KEYPAD_MAX_WIDTH), width: "100%" }}
                >
                  {selectedPayAttempt.shouldShowMessageForRecipientFormField && (
                    <TextInput
                      variant="filled"
                      radius="sm"
                      description="Message for recipient (optional)"
                      value={selectedPayAttempt.userProposedLnurlPaymentComment}
                      onChange={(e) =>
                        selectedPayAttempt.setUserProposedLnurlPaymentComment(
                          e.currentTarget.value,
                        )
                      }
                      maxLength={
                        selectedPayAttempt.lnUrlOneResponse.data
                          .commentLengthAllowed ?? 50
                      }
                      readOnly={
                        selectedPayAttempt.shouldDisableLnurlStageTwoForm
                      }
                      styles={{
                        input: {
                          backgroundColor: "#1e1e1e", // Or use theme.colors.dark[7] if using dark theme
                          color: "#fff",
                          borderColor: "#333",
                        },
                        description: {
                          color: "#888", // Dim gray to match the tone of the keypad
                        },
                      }}
                    />
                  )}
                  <TwoFactorInput
                    visible={selectedPayAttempt.shouldShowTwoFactorInput}
                    code={twoFACode}
                    onCodeChange={setTwoFACode}
                  />

                  <Button
                    disabled={
                      selectedPayAttempt.shouldDisableLnurlStageTwoButton
                    }
                    leftSection={<IconSend2 size={16} />}
                    onClick={() => {
                      const memoed = twoFACode.trim();
                      setTwoFACode("");
                      selectedPayAttempt.continueLnUrlStageTwoPayment(memoed);
                    }}
                  >
                    Send Payment
                  </Button>
                </Stack>
              </Center>
            </>
          )}

          {selectedPayAttempt.shouldShowPayNowButtonForInvoicePayment && (
            <Center>
              <Stack style={{ maxWidth: rem(KEYPAD_MAX_WIDTH), width: "100%" }}>
                <TwoFactorInput
                  visible={selectedPayAttempt.shouldShowTwoFactorInput}
                  code={twoFACode}
                  onCodeChange={setTwoFACode}
                />
                <Button
                  loading={
                    selectedPayAttempt.waitingOnRailsToProvidePayAttemptUuid
                  }
                  loaderProps={{ type: "dots" }}
                  leftSection={<IconSend2 size={16} />}
                  onClick={() => {
                    // console.log(
                    //   "[PAYMENT-FLOW] 🖱️ Send Payment button clicked",
                    //   {
                    //     waitingOnRails:
                    //       selectedPayAttempt.waitingOnRailsToProvidePayAttemptUuid,
                    //     status: selectedPayAttempt.payAttemptStatus,
                    //     bobBalance: selectedBob.safeBalanceInSatoshis,
                    //   }
                    // );
                    selectedPayAttempt.tryToPayInvoiceViaRails(twoFACode);
                  }}
                >
                  Send Payment
                </Button>
              </Stack>
            </Center>
          )}

          {selectedPayAttempt.shouldShowErrorMessageInsteadOfPayButton && (
            <Center>
              <Alert
                color="red"
                title="Insufficient Balance"
                icon={<IconInfoCircle size={16} />}
                style={{ maxWidth: rem(KEYPAD_MAX_WIDTH), width: "100%" }}
              >
                The payment amount (
                {selectedPayAttempt.payAmountSatoshis?.toLocaleString()} sats)
                plus routing fees ({selectedPayAttempt.maxRoutingFeeSatoshis}{" "}
                sats) exceeds your available balance (
                {selectedBob.safeBalanceInSatoshis.toLocaleString()} sats).
              </Alert>
            </Center>
          )}

          <Group mb="lg" justify="center" gap="md" wrap="nowrap">
            {selectedPayAttempt.showStartOverButton && (
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={() => startOver()}
              >
                Start Over
              </Button>
            )}
            {selectedPayAttempt.showAdvancedButton && (
              <Button
                variant="light"
                leftSection={<IconSettings size={16} />}
                onClick={() =>
                  selectedPayAttempt.setAdvancedSettingsAreRevealed(true)
                }
              >
                Advanced Settings
              </Button>
            )}
          </Group>

          {selectedPayAttempt.advancedSettingsAreRevealed && (
            <Center>
              <Stack style={{ maxWidth: rem(KEYPAD_MAX_WIDTH), width: "100%" }}>
                <Group justify="center" gap="xs" mb="md" mt="md">
                  <NumberInput
                    description="Maximum routing fee in satoshis"
                    value={selectedPayAttempt.maxRoutingFeeSatoshis}
                    onChange={(v) => {
                      // Mantine passes a string for empty input and 14+ digit values
                      const n = typeof v === "number" ? v : Number(v);
                      selectedPayAttempt.setMaxRoutingFeeSatoshis(
                        v === "" || !Number.isFinite(n) ? undefined : n,
                      );
                    }}
                    min={1}
                    allowDecimal={false}
                    allowNegative={false}
                    max={selectedPayAttempt.bob.safeBalanceInSatoshis}
                    style={{ flex: 1 }}
                  />
                  <ActionIcon
                    variant="subtle"
                    aria-label="Hide advanced settings"
                    onClick={() =>
                      selectedPayAttempt.setAdvancedSettingsAreRevealed(false)
                    }
                  >
                    <IconX size={18} />
                  </ActionIcon>
                </Group>
              </Stack>
            </Center>
          )}
        </Stack>
      )}
    </>
  );
});

export default Send;
